import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Mail, 
  Send,
  ExternalLink,
  Sparkles,
  Calendar,
  Users,
  Building2,
  Zap
} from "lucide-react";
import { toast } from "sonner";

export const Footer = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error(isRTL ? "يرجى إدخال البريد الإلكتروني" : "Please enter your email");
      return;
    }
    setIsSubmitting(true);
    // Simulate subscription
    setTimeout(() => {
      toast.success(isRTL ? "تم الاشتراك بنجاح!" : "Subscribed successfully!");
      setEmail("");
      setIsSubmitting(false);
    }, 1000);
  };

  const footerLinks = {
    events: [
      { name: isRTL ? "المعارض" : "Exhibitions", path: "/exhibitions" },
      { name: isRTL ? "المؤتمرات والندوات" : "Conferences & Seminars", path: "/conferences-seminars" },
      { name: isRTL ? "المناسبات العامة" : "Public Occasions", path: "/public-occasions" },
    ],
    seasons: [
      { name: isRTL ? "المواسم السعودية" : "Saudi Seasons", path: "/saudi-seasons" },
      { name: isRTL ? "المهرجانات السعودية" : "Saudi Festivals", path: "/saudi-festivals" },
    ],
    community: [
      { name: isRTL ? "مساحات X" : "X Spaces", path: "/x-spaces" },
      { name: isRTL ? "مجموعات التواصل" : "Social Groups", path: "/social-groups" },
    ],
    quickLinks: [
      { name: isRTL ? "الرئيسية" : "Home", path: "/" },
      { name: isRTL ? "أضف فعاليتك" : "Add Your Event", path: "/submit" },
      { name: isRTL ? "الباقات" : "Packages", path: "/packages" },
      { name: isRTL ? "أعلن معنا" : "Advertise", path: "/advertise" },
    ],
  };


  return (
    <footer className="relative overflow-hidden" role="contentinfo" aria-label={isRTL ? "تذييل الموقع" : "Site footer"}>
      {/* Gradient Background */}
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-[#1a0f22] via-[#241530] to-[#1a0f22]" />
      
      {/* Decorative Pattern */}
      <div aria-hidden="true" className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="footerPattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M30 0L60 30L30 60L0 30Z" fill="none" stroke="white" strokeWidth="0.5"/>
              <circle cx="30" cy="30" r="10" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#footerPattern)" />
        </svg>
      </div>

      {/* Glowing Orbs */}
      <div aria-hidden="true" className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
      <div aria-hidden="true" className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />

      <div className="relative z-10">
        {/* Newsletter Section */}
        <section aria-labelledby="newsletter-heading" className="border-b border-white/10">
          <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-2xl mx-auto text-center"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary mb-6">
                <Sparkles className="w-4 h-4" aria-hidden="true" />
                <span className="text-sm font-medium">
                  {isRTL ? "ابق على اطلاع" : "Stay Updated"}
                </span>
              </div>
              
              <h3 id="newsletter-heading" className="text-3xl md:text-4xl font-bold text-white mb-4">
                {isRTL ? "اشترك في نشرتنا البريدية" : "Subscribe to Our Newsletter"}
              </h3>
              <p className="text-gray-300 mb-8 text-lg">
                {isRTL 
                  ? "كن أول من يعلم عن أحدث الفعاليات والمواسم في المملكة" 
                  : "Be the first to know about the latest events and seasons in the Kingdom"}
              </p>
              
              <form
                onSubmit={handleSubscribe}
                aria-label={isRTL ? "نموذج الاشتراك في النشرة البريدية" : "Newsletter subscription form"}
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              >
                <div className="relative flex-1">
                  <label htmlFor="newsletter-email" className="sr-only">
                    {isRTL ? "البريد الإلكتروني" : "Email address"}
                  </label>
                  <Mail aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" />
                  <input
                    id="newsletter-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={isRTL ? "بريدك الإلكتروني" : "Your email address"}
                    className="w-full pl-11 pr-4 py-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/40 text-white placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all"
                    dir={isRTL ? "rtl" : "ltr"}
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isSubmitting}
                  aria-label={isRTL ? "اشترك في النشرة البريدية" : "Subscribe to newsletter"}
                  className="px-8 py-4 bg-gradient-to-r from-secondary to-gold hover:from-secondary/90 hover:to-gold/90 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-gold/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a0f22]"
                >
                  <Send className="w-5 h-5" aria-hidden="true" />
                  <span>{isRTL ? "اشترك الآن" : "Subscribe"}</span>
                </motion.button>
              </form>
              <div role="status" aria-live="polite" className="sr-only">
                {isSubmitting ? (isRTL ? "جاري الاشتراك" : "Subscribing") : ""}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Main Footer Content */}
        <nav
          aria-label={isRTL ? "روابط التذييل" : "Footer navigation"}
          className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-16"
        >
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12">
            {/* Brand Section */}
            <div className="col-span-2 md:col-span-4 lg:col-span-1">
              <Link
                to="/"
                className="inline-block mb-6 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a0f22]"
                aria-label={isRTL ? "دليل يدل - الصفحة الرئيسية" : "Yadel - Homepage"}
              >
                <h2 className="text-2xl font-bold text-white">
                  دليل يدل - Yadel
                </h2>
              </Link>
              <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                {isRTL 
                  ? "منصتك الأولى لاكتشاف أفضل الفعاليات والمواسم والمؤتمرات في المملكة العربية السعودية"
                  : "Your premier platform for discovering the best events, seasons, and conferences in Saudi Arabia"}
              </p>
            </div>

            {/* Events Links */}
            <div>
              <h4 id="footer-events" className="text-white font-semibold mb-5 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" aria-hidden="true" />
                {isRTL ? "الفعاليات" : "Events"}
              </h4>
              <ul className="space-y-3" aria-labelledby="footer-events">
                {footerLinks.events.map((link) => (
                  <li key={link.path}>
                    <Link 
                      to={link.path}
                      className="text-gray-300 hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded transition-colors text-sm flex items-center gap-1 group"
                    >
                      <span>{link.name}</span>
                      <ExternalLink aria-hidden="true" className="w-3 h-3 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Seasons Links */}
            <div>
              <h4 id="footer-seasons" className="text-white font-semibold mb-5 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary" aria-hidden="true" />
                {isRTL ? "المواسم" : "Seasons"}
              </h4>
              <ul className="space-y-3" aria-labelledby="footer-seasons">
                {footerLinks.seasons.map((link) => (
                  <li key={link.path}>
                    <Link 
                      to={link.path}
                      className="text-gray-300 hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded transition-colors text-sm flex items-center gap-1 group"
                    >
                      <span>{link.name}</span>
                      <ExternalLink aria-hidden="true" className="w-3 h-3 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Community Links */}
            <div>
              <h4 id="footer-community" className="text-white font-semibold mb-5 flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" aria-hidden="true" />
                {isRTL ? "المجتمع" : "Community"}
              </h4>
              <ul className="space-y-3" aria-labelledby="footer-community">
                {footerLinks.community.map((link) => (
                  <li key={link.path}>
                    <Link 
                      to={link.path}
                      className="text-gray-300 hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded transition-colors text-sm flex items-center gap-1 group"
                    >
                      <span>{link.name}</span>
                      <ExternalLink aria-hidden="true" className="w-3 h-3 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 id="footer-quicklinks" className="text-white font-semibold mb-5 flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" aria-hidden="true" />
                {isRTL ? "روابط سريعة" : "Quick Links"}
              </h4>
              <ul className="space-y-3" aria-labelledby="footer-quicklinks">
                {footerLinks.quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link 
                      to={link.path}
                      className="text-gray-300 hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded transition-colors text-sm flex items-center gap-1 group"
                    >
                      <span>{link.name}</span>
                      <ExternalLink aria-hidden="true" className="w-3 h-3 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </nav>

        {/* Bottom Bar */}
        <div className="border-t border-white/10">
          <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-300 text-sm">
                {`© ${new Date().getFullYear()} دليل يدل - Yadel - جميع الحقوق محفوظة`}
              </p>
              
              <div className="flex items-center gap-6 text-sm">
                <Link
                  to="/privacy-policy"
                  className="text-gray-300 hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded transition-colors"
                >
                  {isRTL ? "سياسة الخصوصية" : "Privacy Policy"}
                </Link>
                <Link
                  to="/terms-of-service"
                  className="text-gray-300 hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded transition-colors"
                >
                  {isRTL ? "الشروط والأحكام" : "Terms of Service"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
